import React from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import './common-components.css';

// Beautiful Back Button Component
export const BackButton = ({ title = "Back", className = "" }) => {
  const history = useHistory();

  const handleGoBack = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (history.length > 1) {
      history.goBack();
    } else {
      history.push('/');
    }
  };

  return (
    <Button
      variant="light"
      type="button"
      className={`back-button-beautiful ${className}`}
      onClick={handleGoBack}
    >
      <i className="bi bi-arrow-left"></i>
      <span>{title}</span>
    </Button>
  );
};

// Beautiful Loading Component
export const LoadingSpinner = ({ message = "Loading...", fullPage = false }) => {
  return (
    <div className={`loading-container ${fullPage ? 'full-page' : ''}`}>
      <div className="loading-content">
        <div className="loading-spinner">
          <div className="spinner-circle"></div>
          <div className="spinner-circle delay-1"></div>
          <div className="spinner-circle delay-2"></div>
        </div>
        <div className="loading-dots">
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
        </div>
        <p className="loading-message">{message}</p>
      </div>
    </div>
  );
};

// Beautiful No Data Component
export const NoDataFound = ({ 
  title = "No Data Found", 
  message = "There's nothing here yet. Check back later!", 
  icon = "inbox",
  showBackButton = true,
  onBackClick = null,
  actionButton = null 
}) => {
  const history = useHistory();

  const handleGoBack = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onBackClick && typeof onBackClick === 'function') {
      onBackClick();
    } else if (history.length > 1) {
      history.goBack();
    } else {
      history.push('/');
    }
  };

  return (
    <div className="no-data-container">
      <div className="no-data-content">
        {/* Animated Illustration */}
        <div className="no-data-illustration">
          <div className="illustration-circle">
            <div className="illustration-inner">
              <i className={`bi bi-${icon}`}></i>
            </div>
          </div>
          <div className="floating-elements">
            <span className="float-element el-1"><i className="bi bi-search"></i></span>
            <span className="float-element el-2"><i className="bi bi-file-earmark"></i></span>
            <span className="float-element el-3"><i className="bi bi-folder2-open"></i></span>
          </div>
        </div>

        {/* Text Content */}
        <h3 className="no-data-title">{title}</h3>
        <p className="no-data-message">{message}</p>

        {/* Action Buttons */}
        <div className="no-data-actions">
          {showBackButton && (
            <Button
              variant="primary"
              type="button"
              className="no-data-back-btn"
              onClick={handleGoBack}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Go Back
            </Button>
          )}
          {actionButton && actionButton}
        </div>
      </div>
    </div>
  );
};

// Page Header with Back Button
export const PageHeader = ({ 
  title, 
  subtitle, 
  icon = "book",
  showBackButton = true,
  onBackClick = null,
  gradient = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  rightContent = null,
  count = null,
  countLabel = "Items"
}) => {
  const history = useHistory();

  const handleGoBack = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onBackClick && typeof onBackClick === 'function') {
      onBackClick();
    } else if (history.length > 1) {
      history.goBack();
    } else {
      history.push('/');
    }
  };

  return (
    <div className="page-header-beautiful" style={{ background: gradient }}>
      <div className="page-header-left">
        {showBackButton && (
          <button 
            type="button"
            className="header-back-btn"
            onClick={handleGoBack}
            aria-label="Go back"
          >
            <i className="bi bi-arrow-left"></i>
          </button>
        )}
        <div className="page-header-icon">
          <i className={`bi bi-${icon}`}></i>
        </div>
        <div className="page-header-text">
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
      </div>
      <div className="page-header-right">
        {count !== null && (
          <div className="page-header-count">
            <span className="count-number">{count}</span>
            <span className="count-label">{countLabel}</span>
          </div>
        )}
        {rightContent}
      </div>
    </div>
  );
};

// Skeleton Loader for Cards
export const CardSkeleton = ({ count = 6 }) => {
  return (
    <div className="skeleton-grid">
      {[...Array(count)].map((_, i) => (
        <div key={i} className="skeleton-card">
          <div className="skeleton-header shimmer"></div>
          <div className="skeleton-body">
            <div className="skeleton-title shimmer"></div>
            <div className="skeleton-text shimmer"></div>
            <div className="skeleton-button shimmer"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default { BackButton, LoadingSpinner, NoDataFound, PageHeader, CardSkeleton };
